// Minutes tile (companion) — form → AI minutes → EMAIL the notes to yourself (the owner).
// v2 (Gery feedback 2026-06-10): tap a row to OPEN an existing minute (prefilled, preview
// ready, re-send updates the same row) · attendees as chips with a FROM-CONTACTS picker ·
// photo attachments (e.g. a whiteboard) that ride the email · dictation into the raw notes.
import { toast } from "./app";
import { loadSnap, saveSnap, skeletonHtml } from "./cache";
import { confirmDanger } from "./confirm";
import { downscaleToJpegB64 } from "./contacts";
import { fmtDate, fmtTime } from "./locale";

const $ = <T extends Element = HTMLElement>(sel: string): T | null => document.querySelector<T>(sel);

type Attendee = { name?: string; email: string };
type Minute = {
  id: number | string;
  title: string;
  location?: string | null;
  meeting_date?: string | null;
  meeting_time?: string | null;
  attendees: Attendee[];
  body_raw?: string | null;
  body_html?: string | null;
  sent_at?: string | null;
  created_at?: string;

  org_id?: number | null; // org-owned (view-only on the phone — manage on desktop)
};
type Contact = { id?: number | string; name?: string; email: string; company?: string };

let minutes: Minute[] = [];
let companyMeta: { enabled: boolean; orgName: string | null } = { enabled: false, orgName: null };
let loaded = false;
let editId: string | null = null; // open existing → its id (send UPDATEs); null = new
let genHtml = "";
let attendees: Attendee[] = [];
let photos: Array<{ name: string; b64: string }> = [];
const MAX_PHOTO_B64 = 5_000_000; // total, matches the server guard (~3.7 MB of JPEG)

// contacts picker state
let allContacts: Contact[] | null = null;
let picked = new Set<string>(); // emails selected in the picker

// dictation state
let dictMedia: MediaRecorder | null = null;
let dictStream: MediaStream | null = null;
let dictChunks: BlobPart[] = [];
let dictFmt = "webm";

const esc = (s: unknown): string => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]!));

// ── data ──
// Stale-while-revalidate: paint the last-known list (or a skeleton on a true first run)
// immediately, then fetch and re-render. The screen is never blanked while loading.
export async function loadMinutes(force = false): Promise<void> {
  if (loaded && !force) { renderList(); return; }
  let painted = loaded;
  if (!loaded) {
    const snap = loadSnap<Minute[]>("minutes");
    if (snap) { minutes = snap; painted = true; renderList(); }
    else { const list = $("#mnList"); if (list) list.innerHTML = skeletonHtml(); }
  }
  try {
    const r = await fetch("/api/minutes", { credentials: "include" });
    if (r.status === 401) { renderEmpty("This phone isn't linked. Reconnect from Outlook to load your minutes."); return; }
    if (r.status === 402) { renderEmpty("Notes are part of the subscription — manage it in the Outlook add-in."); return; }
    if (!r.ok) throw new Error(String(r.status));
    const j = (await r.json()) as { minutes?: Minute[]; company?: { enabled: boolean; orgName: string | null } };
    minutes = j.minutes || [];
    if (j.company) companyMeta = j.company;
    loaded = true;
    saveSnap("minutes", minutes);
    renderList();
  } catch {
    if (!painted) renderEmpty("Couldn't load your minutes. Check your connection and try again.");
  }
}

function renderEmpty(msg: string): void {
  const list = $("#mnList"), empty = $("#mnEmpty");
  if (list) list.innerHTML = "";
  if (empty) { empty.textContent = msg; empty.style.display = "block"; }
}

function renderList(): void {
  const list = $("#mnList"), empty = $("#mnEmpty");
  if (!list) return;
  const term = ($("#mnSearch") as HTMLInputElement | null)?.value.trim().toLowerCase() || "";
  const clear = $("#mnClear") as HTMLElement | null;
  if (clear) clear.hidden = !term;

  // Active-screen gate: the Home-boot prefetch renders in the background (see contacts.ts).
  const count = $("#topCount");
  if (count && $("#screen-minutes")?.classList.contains("active")) { count.textContent = minutes.length ? String(minutes.length) : ""; count.hidden = !minutes.length; }

  const items = term
    ? minutes.filter((m) => `${m.title} ${(m.attendees || []).map((a) => a.name || a.email || "").join(" ")}`.toLowerCase().includes(term))
    : minutes;
  if (!items.length) {
    list.innerHTML = "";
    if (empty) { empty.textContent = term ? "No minutes match." : "No minutes yet. Tap New to write up a meeting."; empty.style.display = "block"; }
    return;
  }
  if (empty) empty.style.display = "none";
  list.innerHTML = items
    .map((m) => {
      const meta = [
        m.meeting_date ? fmtDate(m.meeting_date) : "",
        m.meeting_time ? fmtTime(m.meeting_time) : "",
        m.attendees?.length ? `${m.attendees.length} attendee${m.attendees.length === 1 ? "" : "s"}` : "",
      ].filter(Boolean).join(" · ");
      // a DIV row (not <button>) so the delete button can nest — same shape as Tasks rows
      return (
        `<div class="mn-row" data-open="${m.id}" role="button" tabindex="0">` +
        `<span class="mn-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6M8 13h8M8 17h5"/></svg></span>` +
        `<span class="mn-info"><span class="mn-title">${esc(m.title)}${m.org_id != null ? ` <span class="vf-chip-company">${esc(companyMeta.orgName || "Company")}</span>` : ""}</span>${meta ? `<span class="mn-sub">${esc(meta)}</span>` : ""}</span>` +
        (m.sent_at ? `<span class="mn-sent"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg> Sent</span>` : "") +
        `<button class="row-del" data-del="${m.id}" title="Delete minutes"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg></button>` +
        `</div>`
      );
    })
    .join("");
}

async function removeMinute(id: string): Promise<void> {
  const m = minutes.find((x) => String(x.id) === id);
  const yes = await confirmDanger({
    title: "Delete these minutes?",
    body: `"${m?.title || "Untitled"}" disappears from every device. This cannot be undone.`,
    cta: "Delete minutes",
  });
  if (!yes) return;
  try {
    const r = await fetch(`/api/minutes?id=${encodeURIComponent(id)}`, { method: "DELETE", credentials: "include" });
    if (!r.ok) { toast("Couldn't delete — try again"); return; }
    toast("Notes deleted");
    void loadMinutes(true);
  } catch { toast("Couldn't delete — check your connection"); }
}

// ── attendees (chips + free-typed input + contacts picker) ──
function renderAttendees(): void {
  const host = $("#mnAttChips");
  if (!host) return;
  host.innerHTML = attendees
    .map((a, i) =>
      `<span class="mn-chip">${esc(a.name || a.email)}<button type="button" class="mn-chip-x" data-rm-att="${i}" title="Remove"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg></button></span>`,
    )
    .join("");
}
function addAttendee(email: string, name?: string): void {
  const e = email.trim().toLowerCase();
  if (!e || !e.includes("@")) return;
  if (attendees.some((a) => (a.email || "").toLowerCase() === e)) return;
  attendees.push({ email: e, ...(name ? { name } : {}) });
  renderAttendees();
}
function commitTypedAttendee(): void {
  const inp = $("#mnfAttendees") as HTMLInputElement | null;
  if (!inp) return;
  inp.value.split(/[,;\s]+/).forEach((p) => addAttendee(p));
  inp.value = "";
}

// contacts picker (bottom sheet, search + multi-select from /api/contacts)
async function openPicker(): Promise<void> {
  picked = new Set();
  $("#mnPickScrim")?.classList.add("open");
  $("#mnPick")?.classList.add("open");
  const inp = $("#mnPickSearch") as HTMLInputElement | null;
  if (inp) inp.value = "";
  if (!allContacts) {
    const host = $("#mnPickList");
    if (host) host.innerHTML = skeletonHtml(5);
    try {
      const r = await fetch("/api/contacts", { credentials: "include" });
      const j = (await r.json().catch(() => ({}))) as { items?: Contact[] };
      allContacts = (j.items || []).filter((c) => c.email);
    } catch { allContacts = []; }
  }
  renderPicker();
}
function closePicker(): void { $("#mnPickScrim")?.classList.remove("open"); $("#mnPick")?.classList.remove("open"); }
function renderPicker(): void {
  const host = $("#mnPickList");
  if (!host) return;
  const q = (($("#mnPickSearch") as HTMLInputElement | null)?.value || "").trim().toLowerCase();
  const existing = new Set(attendees.map((a) => (a.email || "").toLowerCase()));
  const rows = (allContacts || [])
    .filter((c) => !!c.email && !existing.has(c.email.toLowerCase()))
    .filter((c) => !q || `${c.name || ""} ${c.email} ${c.company || ""}`.toLowerCase().includes(q))
    .slice(0, 40);
  host.innerHTML = rows.length
    ? rows
        .map((c) => {
          const on = picked.has((c.email || "").toLowerCase());
          return (
            `<button type="button" class="mn-pick-row${on ? " on" : ""}" data-pick="${esc(c.email)}" data-pick-name="${esc(c.name || "")}">` +
            `<span class="mn-pick-info"><span class="mn-pick-name">${esc(c.name || c.email)}</span><span class="mn-pick-sub">${esc([c.company, c.email].filter(Boolean).join(" · "))}</span></span>` +
            `<span class="mn-pick-ck">${on ? `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>` : ""}</span>` +
            `</button>`
          );
        })
        .join("")
    : `<div class="cpane-empty">${q ? "No contacts match." : "No saved contacts yet."}</div>`;
  const btn = $("#mnPickAdd") as HTMLButtonElement | null;
  if (btn) { btn.disabled = !picked.size; btn.textContent = picked.size ? `Add (${picked.size})` : "Add"; }
}

// ── photo attachments ──
function renderPhotos(): void {
  const host = $("#mnPhotos");
  if (!host) return;
  host.innerHTML = photos
    .map((p, i) =>
      `<span class="mn-ph"><img src="data:image/jpeg;base64,${p.b64}" alt="" /><button type="button" class="mn-ph-x" data-rm-ph="${i}" title="Remove"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg></button></span>`,
    )
    .join("");
}
async function onPhoto(input: HTMLInputElement): Promise<void> {
  const file = input.files?.[0];
  input.value = "";
  if (!file) return;
  try {
    const b64 = await downscaleToJpegB64(file, 1600, 0.82);
    const total = photos.reduce((n, p) => n + p.b64.length, 0) + b64.length;
    if (total > MAX_PHOTO_B64) { toast("That's too many photos for one email"); return; }
    photos.push({ name: file.name && /\.(jpe?g|png|heic|heif|webp)$/i.test(file.name) ? file.name.replace(/\.(png|heic|heif|webp)$/i, ".jpg") : `photo-${photos.length + 1}.jpg`, b64 });
    renderPhotos();
  } catch { toast("Couldn't read that photo"); }
}

// ── dictation into the raw notes ──
function pickMime(): { mime: string; f: string } {
  const cands = [
    { mime: "audio/webm;codecs=opus", f: "webm" }, { mime: "audio/webm", f: "webm" },
    { mime: "audio/mp4", f: "mp4" }, { mime: "audio/ogg;codecs=opus", f: "ogg" },
  ];
  for (const c of cands) if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported?.(c.mime)) return c;
  return { mime: "", f: "webm" };
}
function setDict(s: "idle" | "rec" | "busy"): void { $("#mnDict")?.setAttribute("data-dict", s); }
async function toggleDict(): Promise<void> {
  const s = $("#mnDict")?.getAttribute("data-dict") || "idle";
  if (s === "busy") return;
  if (s === "rec") { try { dictMedia?.stop(); } catch { /* onstop */ } return; }
  try { dictStream = await navigator.mediaDevices.getUserMedia({ audio: true }); }
  catch { toast("We couldn't access your microphone"); return; }
  const { mime, f } = pickMime();
  dictFmt = f; dictChunks = [];
  try { dictMedia = mime ? new MediaRecorder(dictStream, { mimeType: mime }) : new MediaRecorder(dictStream); }
  catch { dictMedia = new MediaRecorder(dictStream!); }
  dictMedia.ondataavailable = (ev) => { if (ev.data?.size) dictChunks.push(ev.data); };
  dictMedia.onstop = () => void dictFinish();
  dictMedia.start();
  setDict("rec");
}
async function dictFinish(): Promise<void> {
  const blob = new Blob(dictChunks);
  dictChunks = [];
  dictStream?.getTracks().forEach((t) => t.stop()); dictStream = null; dictMedia = null;
  if (blob.size < 1200) { setDict("idle"); return; }
  setDict("busy");
  try {
    const b64 = await new Promise<string>((res, rej) => {
      const fr = new FileReader();
      fr.onload = () => { const s = String(fr.result || ""); res(s.slice(s.indexOf(",") + 1)); };
      fr.onerror = () => rej(new Error("read_failed"));
      fr.readAsDataURL(blob);
    });
    const r = await fetch("/api/dictate", {
      method: "POST", headers: { "content-type": "application/json" }, credentials: "include",
      body: JSON.stringify({ audio_b64: b64, audio_format: dictFmt }),
    });
    const j = (await r.json().catch(() => ({}))) as { text?: string; error?: string };
    if (j.text) {
      const ta = $("#mnfNotes") as HTMLTextAreaElement | null;
      if (ta) ta.value = (ta.value ? ta.value.trimEnd() + "\n" : "") + j.text;
    } else if (j.error === "no_speech") toast("We couldn't hear any speech — try again");
    else toast("Dictation failed — try again");
  } catch { toast("Dictation failed — check your connection"); }
  finally { setDict("idle"); }
}

// ── editor (form ↔ preview in one overlay) ──
function openEditor(m?: Minute | null): void {
  editId = m ? String(m.id) : null;
  genHtml = m?.body_html || "";
  attendees = Array.isArray(m?.attendees) ? m!.attendees.filter((a) => a?.email).map((a) => ({ email: a.email, ...(a.name ? { name: a.name } : {}) })) : [];
  photos = [];
  const t = $("#mnEdTitle"); if (t) t.textContent = m ? "Note" : "New note";
  ($("#mnfTitle") as HTMLInputElement | null)!.value = m?.title || "";
  ($("#mnfDate") as HTMLInputElement | null)!.value = m?.meeting_date ? String(m.meeting_date).slice(0, 10) : new Date().toISOString().slice(0, 10);
  ($("#mnfTime") as HTMLInputElement | null)!.value = m?.meeting_time || "";
  ($("#mnfLocation") as HTMLInputElement | null)!.value = m?.location || "";
  ($("#mnfAttendees") as HTMLInputElement | null)!.value = "";
  ($("#mnfNotes") as HTMLTextAreaElement | null)!.value = m?.body_raw || "";
  setDict("idle");
  renderAttendees();
  renderPhotos();
  if (genHtml) showPreview(); else showForm();
  $("#mnEditor")?.classList.add("open");
  setTimeout(() => { openSnap = formSnap(); }, 0); // seed after the fields landed in the DOM
}
/** Home-search result tap → fetch the minute by id and open its editor. */
export function openMinuteById(id: string): void {
  void openExisting(id);
}
/** C360 "+ Note" (spec §8a, phone port): a fresh note with the contact pre-attached as attendee. */
export function openMinuteFor(att: { email: string; name?: string | null }): void {
  openEditor(null);
  addAttendee(att.email, att.name || undefined);
}
async function openExisting(id: string): Promise<void> {
  try {
    const r = await fetch(`/api/minutes?id=${encodeURIComponent(id)}`, { credentials: "include" });
    if (!r.ok) { toast("Couldn't open that minute"); return; }
    const j = (await r.json()) as { minute?: Minute };
    if (j.minute) openEditor(j.minute);
  } catch { toast("Couldn't open that minute"); }
}
// SAVE-CANON C1: leaving the editor SAVES (close == save, the desktop Notes model) — previously
// the phone's only write was /send, so an unmailed note was gone. openSnap makes an untouched
// exit a no-op; a NEW note that gets content is created via POST, edits PATCH per record.
let openSnap = "";
const formSnap = (): string => { const f = readForm() as Record<string, unknown>; delete f.id; return JSON.stringify({ ...f, body_html: genHtml }); };
async function saveNote(): Promise<boolean> {
  const f = readForm();
  const hasContent = !!(f.title || f.notes || attendees.length);
  if (!hasContent || formSnap() === openSnap) return true; // nothing (new) to keep / unchanged
  try {
    if (editId) {
      const r = await fetch(`/api/minutes/${encodeURIComponent(editId)}`, {
        method: "PATCH", headers: { "content-type": "application/json" }, credentials: "include",
        body: JSON.stringify({ ...f, body_html: genHtml || null }),
      });
      if (!r.ok) throw new Error(String(r.status));
    } else {
      const r = await fetch("/api/minutes", {
        method: "POST", headers: { "content-type": "application/json" }, credentials: "include",
        body: JSON.stringify({ ...f, body_html: genHtml || null }),
      });
      const j = (await r.json().catch(() => ({}))) as { id?: number };
      if (!r.ok || !j.id) throw new Error(String(r.status));
      editId = String(j.id);
      toast("Note saved");
    }
    openSnap = formSnap();
    return true;
  } catch { toast("Couldn't save the note — check your connection"); return false; }
}
async function flushClose(): Promise<void> { await saveNote(); closeEditor(); void loadMinutes(true); }

function closeEditor(): void {
  try { if (dictMedia && dictMedia.state !== "inactive") dictMedia.stop(); } catch { /* ignore */ }
  dictStream?.getTracks().forEach((t) => t.stop()); dictStream = null;
  $("#mnEditor")?.classList.remove("open");
}
function showForm(): void { $("#mnForm")!.style.display = "flex"; $("#mnPreview")!.style.display = "none"; }
function showPreview(): void {
  const body = $("#mnPreviewBody");
  if (body) body.innerHTML = genHtml;
  $("#mnForm")!.style.display = "none";
  $("#mnPreview")!.style.display = "flex";
}

function readForm() {
  commitTypedAttendee(); // anything still typed in the input counts too
  return {
    ...(editId ? { id: editId } : {}),
    title: ($("#mnfTitle") as HTMLInputElement | null)?.value.trim() || "",
    meeting_date: ($("#mnfDate") as HTMLInputElement | null)?.value || "",
    meeting_time: ($("#mnfTime") as HTMLInputElement | null)?.value || "",
    location: ($("#mnfLocation") as HTMLInputElement | null)?.value.trim() || "",
    attendees,
    notes: ($("#mnfNotes") as HTMLTextAreaElement | null)?.value.trim() || "",
  };
}

async function generate(): Promise<void> {
  const f = readForm();
  if (!f.notes) { toast("Add some meeting notes first"); return; }
  const btn = $("#mnGenerate") as HTMLButtonElement | null;
  if (btn) { btn.disabled = true; btn.textContent = "Generating…"; }
  try {
    const r = await fetch("/api/minutes/generate", {
      method: "POST", headers: { "content-type": "application/json" }, credentials: "include",
      body: JSON.stringify(f),
    });
    const j = (await r.json().catch(() => ({}))) as { html?: string; error?: string };
    if (!r.ok || !j.html) { toast(j.error === "subscription_required" ? "Subscription required" : "Couldn't generate — try again"); return; }
    genHtml = j.html;
    showPreview();
  } catch { toast("Couldn't generate — check your connection"); }
  finally { if (btn) { btn.disabled = false; btn.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 3v4M3 5h4M6 17v4M4 19h4M13 3l4 18M9 21h8"/></svg> Generate minutes`; } }
}

async function send(): Promise<void> {
  if (!genHtml) return;
  const f = readForm();
  const btn = $("#mnSend") as HTMLButtonElement | null;
  if (btn) { btn.disabled = true; btn.textContent = "Sending…"; }
  try {
    const r = await fetch("/api/minutes/send", {
      method: "POST", headers: { "content-type": "application/json" }, credentials: "include",
      body: JSON.stringify({
        ...f, html: genHtml,
        attachments: photos.map((p) => ({ filename: p.name, content: p.b64 })),
      }),
    });
    const j = (await r.json().catch(() => ({}))) as { ok?: boolean; sent_to?: number; error?: string };
    if (!r.ok || !j.ok) { toast(j.error === "attachments_too_large" ? "Photos too large for one email" : "Couldn't send — try again"); return; }
    closeEditor();
    toast("Notes emailed to you");
    await loadMinutes(true);
  } catch { toast("Couldn't send — check your connection"); }
  finally { if (btn) { btn.disabled = false; btn.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 2 11 13"/><path d="M22 2 15 22l-4-9-9-4z"/></svg> Email to me`; } }
}

// ── wiring ──
export function initMinutes(): void {
  $("#mnNew")?.addEventListener("click", () => openEditor(null));
  const search = $("#mnSearch") as HTMLInputElement | null;
  search?.addEventListener("input", renderList);
  $("#mnClear")?.addEventListener("click", () => { if (search) search.value = ""; renderList(); search?.focus(); });
  $("#mnList")?.addEventListener("click", (e) => {
    const del = (e.target as HTMLElement).closest<HTMLElement>("[data-del]");
    if (del) { void removeMinute(del.getAttribute("data-del")!); return; }
    const row = (e.target as HTMLElement).closest<HTMLElement>("[data-open]");
    if (row) void openExisting(row.getAttribute("data-open")!);
  });
  $("#mnEdBack")?.addEventListener("click", () => void flushClose());
  $("#mnGenerate")?.addEventListener("click", () => void generate());
  $("#mnPreviewBack")?.addEventListener("click", showForm);
  $("#mnSend")?.addEventListener("click", () => void send());

  // attendees: typed input (Enter/comma) + chips removal + the contacts picker
  const att = $("#mnfAttendees") as HTMLInputElement | null;
  att?.addEventListener("keydown", (e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); commitTypedAttendee(); } });
  att?.addEventListener("blur", commitTypedAttendee);
  $("#mnAttChips")?.addEventListener("click", (e) => {
    const x = (e.target as HTMLElement).closest<HTMLElement>("[data-rm-att]");
    if (x) { attendees.splice(Number(x.getAttribute("data-rm-att")), 1); renderAttendees(); }
  });
  $("#mnFromContacts")?.addEventListener("click", () => void openPicker());
  $("#mnPickScrim")?.addEventListener("click", closePicker);
  $("#mnPickClose")?.addEventListener("click", closePicker);
  $("#mnPickSearch")?.addEventListener("input", renderPicker);
  $("#mnPickList")?.addEventListener("click", (e) => {
    const row = (e.target as HTMLElement).closest<HTMLElement>("[data-pick]");
    if (!row) return;
    const email = (row.getAttribute("data-pick") || "").toLowerCase();
    if (picked.has(email)) picked.delete(email); else picked.add(email);
    renderPicker();
  });
  $("#mnPickAdd")?.addEventListener("click", () => {
    (allContacts || []).forEach((c) => { if (c.email && picked.has(c.email.toLowerCase())) addAttendee(c.email, c.name); });
    closePicker();
  });

  // photos
  $("#mnAddPhoto")?.addEventListener("click", () => ($("#mnPhotoCam") as HTMLInputElement | null)?.click());
  $("#mnAddPhotoLib")?.addEventListener("click", () => ($("#mnPhotoLib") as HTMLInputElement | null)?.click());
  $("#mnPhotoCam")?.addEventListener("change", (e) => void onPhoto(e.target as HTMLInputElement));
  $("#mnPhotoLib")?.addEventListener("change", (e) => void onPhoto(e.target as HTMLInputElement));
  $("#mnPhotos")?.addEventListener("click", (e) => {
    const x = (e.target as HTMLElement).closest<HTMLElement>("[data-rm-ph]");
    if (x) { photos.splice(Number(x.getAttribute("data-rm-ph")), 1); renderPhotos(); }
  });

  // dictation
  $("#mnDict")?.addEventListener("click", () => void toggleDict());
}
